export interface FkMapping {
  resource: string
  labelField: string
  /** Additional fields to fetch from PostgREST (appended to select). */
  extraFields?: string[]
  /** PostgREST embedded resource select clause for composite labels. */
  embeddedSelect?: string
  /** Compose a display label from the loaded record. Falls back to labelField. */
  formatLabel?: (record: Record<string, unknown>) => string
  /** Field to filter against when searching (defaults to labelField). */
  searchField?: string
}

// ─── Override map ──────────────────────────────────────────────────────────────

export const FK_OVERRIDES: Record<string, FkMapping> = {
  company_id: { resource: 'companies', labelField: 'company_name' },

  branch_id: {
    resource: 'branches',
    labelField: 'name',
    embeddedSelect: 'id,name,companies(company_name)',
    formatLabel: (r) => {
      const co = (r.companies as Record<string, unknown> | undefined)?.company_name
      return co ? `${r.name} (${co})` : String(r.name)
    },
  },

  agency_id: { resource: 'agencies', labelField: 'name' },

  admin_user_id: { resource: 'users', labelField: 'email', searchField: 'email' },
  actor_user_id: { resource: 'users', labelField: 'email', searchField: 'email' },
  scoped_agency_id: { resource: 'companies', labelField: 'company_name', searchField: 'company_name' },
  scoped_company_id: { resource: 'companies', labelField: 'company_name', searchField: 'company_name' },

  plan_id: { resource: 'plans', labelField: 'name' },

  user_id: { resource: 'users', labelField: 'email', extraFields: ['fullname'], formatLabel: (r: Record<string, unknown>) => r.fullname ? String(r.fullname) : String(r.email) },

  role_id: {
    resource: 'company_roles',
    labelField: 'role_name',
    embeddedSelect: 'id,role_name,companies(company_name)',
    formatLabel: (r) => {
      const co = (r.companies as Record<string, unknown> | undefined)?.company_name
      return co ? `${r.role_name} (${co})` : String(r.role_name)
    },
  },

  manager_id: {
    resource: 'company_users',
    labelField: 'user_title',
    embeddedSelect: 'id,user_title,users(email),company_roles(role_name),companies(company_name)',
    formatLabel: (r) => {
      const email = (r.users as Record<string, unknown> | undefined)?.email
      const role  = (r.company_roles as Record<string, unknown> | undefined)?.role_name
      const co    = (r.companies as Record<string, unknown> | undefined)?.company_name
      const parts = [email ?? r.user_title, role, co].filter(Boolean)
      return parts.join(' · ')
    },
  },

  company_user_id: {
    resource: 'company_users',
    labelField: 'user_title',
    embeddedSelect: 'id,user_title,users(email),companies(company_name)',
    formatLabel: (r) => {
      const email = (r.users as Record<string, unknown> | undefined)?.email
      const co    = (r.companies as Record<string, unknown> | undefined)?.company_name
      const parts = [email ?? r.user_title, co].filter(Boolean)
      return parts.join(' · ')
    },
  },

  department_id: {
    resource: 'departments',
    labelField: 'name',
    embeddedSelect: 'id,name,branches(companies(company_name))',
    formatLabel: (r) => {
      const br  = r.branches as Record<string, unknown> | undefined
      const co  = (br?.companies as Record<string, unknown> | undefined)?.company_name
      return co ? `${r.name} (${co})` : String(r.name)
    },
  },

  position_id: { resource: 'company_positions', labelField: 'title' },

  application_id: { resource: 'applications', labelField: 'id' },

  subscription_id: { resource: 'subscriptions', labelField: 'id' },

  onboarding_service_id: {
    resource: 'onboarding_services',
    labelField: 'service_name',
    embeddedSelect: 'id,service_name,companies(company_name)',
    formatLabel: (r) => {
      const co = (r.companies as Record<string, unknown> | undefined)?.company_name
      return co ? `${r.service_name} (${co})` : String(r.service_name)
    },
  },

  candidate_id: {
    resource: 'local_candidates',
    labelField: 'name',
    formatLabel: (r: Record<string, unknown>) => String(r.name || r.email || r.id),
  },

  local_candidate_id: {
    resource: 'local_candidates',
    labelField: 'name',
    embeddedSelect: 'id,name,email,companies(company_name)',
    formatLabel: (r) => {
      const co = (r.companies as Record<string, unknown> | undefined)?.company_name
      const display = (r.name || r.email) ?? r.id
      return co ? `${display} (${co})` : String(display)
    },
    searchField: 'name',
  },

  stage_id: {
    resource: 'position_stages',
    labelField: 'stage_name',
  },

  prev_stage_id: {
    resource: 'position_stages',
    labelField: 'stage_name',
  },

  position_stage_id: {
    resource: 'position_stages',
    labelField: 'stage_name',
  },

  company_stage_id: {
    resource: 'company_stages',
    labelField: 'stage_name',
  },

  requisition_id: {
    resource: 'requisitions',
    labelField: 'title',
    formatLabel: (r) => r.title ? `#${r.id} ${r.title}` : String(r.id),
    searchField: 'title',
  },

  requester_id: {
    resource: 'users',
    labelField: 'email',
    extraFields: ['fullname'],
    formatLabel: (r) => r.fullname ? `${r.fullname} (${r.email})` : String(r.email),
  },

  current_approver_id: {
    resource: 'users',
    labelField: 'email',
    extraFields: ['fullname'],
    formatLabel: (r) => r.fullname ? `${r.fullname} (${r.email})` : String(r.email),
  },

  organization_id: {
    resource: 'services_count',
    labelField: 'organization_name',
    formatLabel: (r) => `#${r.id} ${r.organization_name ?? ''}`.trim(),
    searchField: 'organization_name',
  },

  org_chart_position_id: {
    resource: 'org_chart_positions',
    labelField: 'title',
    formatLabel: (r) => `#${r.id} ${r.title ?? ''}`.trim(),
  },

  hr_manager_id: {
    resource: 'users',
    labelField: 'email',
    extraFields: ['fullname'],
    formatLabel: (r) => r.fullname ? `${r.fullname} (${r.email})` : String(r.email),
  },

  created_by: { resource: 'users', labelField: 'email' },

  api_key_id: {
    resource: 'partner_api_keys',
    labelField: 'customer_name',
  },
}

/**
 * Dependent FK filters: when the `dependsOn` form field has a value, the
 * target FK select is restricted with `filterField = <value>`.
 */
export const FK_DEPENDENT_FILTERS: Record<
  string,
  Record<string, { filterField: string; dependsOn: string }>
> = {
  company_users: {
    role_id:       { filterField: 'company_id', dependsOn: 'company_id' },
    manager_id:    { filterField: 'company_id', dependsOn: 'company_id' },
    branch_id:     { filterField: 'company_id', dependsOn: 'company_id' },
    department_id: { filterField: 'branch_id',  dependsOn: 'branch_id' },
  },
}

// ─── Resolver ─────────────────────────────────────────────────────────────────

/**
 * Returns FK mapping for a field ending in `_id`, or null.
 * Checks FK_OVERRIDES first; naive fallback for unknown IDs (stem + "s").
 */
export function resolveFkMapping(fieldName: string): FkMapping | null {
  if (!fieldName.endsWith('_id')) return null
  if (FK_OVERRIDES[fieldName]) return FK_OVERRIDES[fieldName]
  const stem = fieldName.slice(0, -3)
  return { resource: `${stem}s`, labelField: 'name' }
}
